import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Cloud, Database, Users, ArrowRight, Code2 } from 'lucide-react';
import SectionWrapper from '../shared/SectionWrapper';
import { FloatingOrbs, FloatingParticles } from '../shared/FloatingElements';

/* ------------------------------------------------------------------ */
/*  Animation variants                                                 */
/* ------------------------------------------------------------------ */
const stagger = {
  parent: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.15 },
    },
  },
  child: {
    hidden: { opacity: 0, y: 28 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
    },
  },
};

/* ------------------------------------------------------------------ */
/*  Integration categories data                                        */
/* ------------------------------------------------------------------ */
const categories = [
  {
    icon: Building2,
    title: 'ERP Systems',
    items: ['SAP', 'Oracle', 'Microsoft Dynamics', 'NetSuite'],
  },
  {
    icon: Cloud,
    title: 'Cloud Storage',
    items: ['AWS S3', 'Azure Blob', 'Google Cloud', 'Dropbox'],
  },
  {
    icon: Database,
    title: 'Data Sources',
    items: ['PostgreSQL', 'MySQL', 'MongoDB', 'REST APIs'],
  },
  {
    icon: Users,
    title: 'Collaboration',
    items: ['Microsoft Teams', 'Slack', 'Email', 'Webhooks'],
  },
];

/* ------------------------------------------------------------------ */
/*  Integration Category Card                                          */
/* ------------------------------------------------------------------ */
const CategoryCard = ({ category, index = 0 }) => {
  const Icon = category.icon;

  return (
    <motion.div
      variants={stagger.child}
      className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-gray-100 dark:border-slate-700 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group"
    >
      {/* Flat bordered icon box */}
      <div className="relative inline-flex items-center justify-center w-[52px] h-[52px] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/50 overflow-hidden">
        <div className="absolute bottom-0 inset-x-0 h-[2px] bg-emerald-400/60" />
        <Icon className="h-7 w-7 text-gray-900 dark:text-gray-100" strokeWidth={1.4} />
      </div>

      {/* Title */}
      <h3 className="mt-4 text-base font-bold text-gray-900 dark:text-white tracking-tight">
        {category.title}
      </h3>

      {/* Integration items as chips */}
      <div className="mt-4 flex flex-wrap gap-2">
        {category.items.map((item) => (
          <span
            key={item}
            className="inline-flex items-center rounded-full bg-gray-50 dark:bg-slate-700 border border-gray-100 dark:border-slate-600 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-slate-300 group-hover:bg-gray-100/80 dark:group-hover:bg-slate-600/80 transition-colors duration-200"
          >
            {item}
          </span>
        ))}
      </div>
    </motion.div>
  );
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
const IntegrationsSection = () => {
  return (
    <SectionWrapper
      id="integrations"
      bgClass="bg-[#fafbfc] dark:bg-[#0a0f1a]"
      padding="py-10 md:py-12"
      className="relative"
    >
      {/* Floating background decorations */}
      <FloatingOrbs
        orbs={[
          { size: 240, color: 'rgba(99,102,241,0.05)', top: '0%', left: '78%', drift: 22, duration: 10, delay: 0 },
          { size: 200, color: 'rgba(59,130,246,0.04)', top: '55%', left: '-4%', drift: 18, duration: 11, delay: 2.5 },
        ]}
      />
      <FloatingParticles count={20} color="#6366f1" theme="light" />

      {/* Header */}
      <motion.div
        className="text-center mb-14"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Pill badge */}
        <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-700/60 px-4 py-1.5 text-xs font-semibold tracking-wide text-indigo-600 dark:text-indigo-400 uppercase">
          <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
          Seamless Integrations
        </span>

        <h2
          className="mt-6 font-extrabold tracking-tight text-gray-900 dark:text-white leading-tight"
          style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)' }}
        >
          Connects With Your{' '}
          <span className="bg-gradient-to-r from-indigo-600 to-blue-500 bg-clip-text text-transparent">
            Existing Stack
          </span>
        </h2>

        <p className="mt-4 max-w-lg mx-auto text-base text-gray-500 dark:text-slate-400 leading-relaxed">
          100+ pre-built connectors. Custom API available.
        </p>
      </motion.div>

      {/* Grid: categories + custom API callout */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5"
        variants={stagger.parent}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-80px' }}
      >
        {/* Category cards (4 cards spanning first 4 columns on lg) */}
        {categories.map((category, idx) => (
          <CategoryCard key={category.title} category={category} index={idx} />
        ))}

        {/* Custom API callout card */}
        <motion.div
          variants={stagger.child}
          className="relative bg-white dark:bg-slate-800 rounded-2xl p-6 border border-gray-100 dark:border-slate-700 md:col-span-2 lg:col-span-1 flex flex-col justify-between overflow-hidden"
          style={{
            backgroundImage:
              'linear-gradient(135deg, rgba(99,102,241,0.03) 0%, rgba(59,130,246,0.03) 100%)',
          }}
        >
          {/* Gradient border accent (top) */}
          <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-400 rounded-t-2xl" />

          {/* Icon */}
          <div className="mt-2">
            <div className="relative inline-flex items-center justify-center w-[52px] h-[52px] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/50 overflow-hidden">
              <div className="absolute bottom-0 inset-x-0 h-[2px] bg-emerald-400/60" />
              <Code2 className="h-7 w-7 text-gray-900 dark:text-gray-100" strokeWidth={1.4} />
            </div>
          </div>

          {/* Content */}
          <div className="mt-4 flex-1">
            <h3 className="text-base font-bold text-gray-900 dark:text-white tracking-tight">
              Custom API
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-gray-500 dark:text-slate-400">
              Need a custom integration? Our API makes it easy.
            </p>
          </div>

          {/* Link */}
          <div className="mt-5">
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 group cursor-pointer">
              Explore API docs
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </span>
          </div>

          {/* Decorative pattern */}
          <div className="absolute -bottom-4 -right-4 w-24 h-24 opacity-[0.05] pointer-events-none">
            <div className="w-full h-full rounded-tl-[50px] bg-gradient-to-br from-indigo-500 to-blue-500" />
          </div>
        </motion.div>
      </motion.div>
    </SectionWrapper>
  );
};

export default IntegrationsSection;
